import { useEffect, useState } from 'react';
import { emailHealthApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Mail,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Send,
  RefreshCw,
} from 'lucide-react';

/**
 * Admin → Configuration card for SMTP health and one-click test send.
 * Lets admins confirm email config (SendGrid key, sender domain) after
 * any secret rotation or re-deploy without waiting for a real user.
 */
export const EmailHealthCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await emailHealthApi.status();
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTest = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const res = await emailHealthApi.test(testEmail || null);
      setLastResult(res.data);
      if (res.data.success) {
        toast.success(`Test email sent to ${res.data.to}`);
        await load();
      } else {
        toast.error('Test send failed — see details below');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Network error';
      setLastResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const healthy = status && status.smtp_configured && status.failures_last_hour === 0;

  return (
    <Card data-testid="email-health-card" className={!healthy && status ? 'border-amber-300' : ''}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-700" />
              Email Configuration &amp; Health
            </CardTitle>
            <CardDescription>
              Verify SMTP/SendGrid setup. Send a test email to confirm delivery after key
              rotation or re-deploy.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="email-health-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status summary */}
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : !status ? (
          <div className="text-sm text-slate-500">Status unavailable.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="text-xs text-slate-500 uppercase tracking-wide">SMTP Host</div>
              <div className="font-mono text-sm text-slate-900 mt-0.5">
                {status.smtp_host || '(not set)'}
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="text-xs text-slate-500 uppercase tracking-wide">From Address</div>
              <div className="font-mono text-sm text-slate-900 mt-0.5">
                {status.smtp_from || '(not set)'}
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Status</div>
              <div className="mt-1">
                {healthy ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Healthy
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                    <AlertTriangle className="w-3 h-3" /> Issues detected
                  </Badge>
                )}
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Failures (24h)</div>
              <div className="font-mono text-sm text-slate-900 mt-0.5">
                {status.failures_last_24h}
                {status.failures_last_hour > 0 && (
                  <span className="text-red-600 text-xs ml-2">
                    ({status.failures_last_hour} in last hour)
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Test send */}
        <div className="border-t pt-4 space-y-2">
          <Label htmlFor="email-test-recipient">Send a test email</Label>
          <div className="flex gap-2 flex-wrap">
            <Input
              id="email-test-recipient"
              type="email"
              placeholder="Leave blank to send to your own email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={sending}
              className="flex-1 min-w-[240px]"
              data-testid="email-test-recipient"
            />
            <Button
              onClick={handleTest}
              disabled={sending}
              data-testid="email-test-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </>
              )}
            </Button>
          </div>

          {lastResult && (
            <div
              data-testid="email-test-result"
              className={`mt-2 rounded-md p-3 text-sm ${
                lastResult.success
                  ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                  : 'bg-red-50 text-red-900 border border-red-200'
              }`}
            >
              {lastResult.success ? (
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5" />
                  <div>
                    <div className="font-semibold">Test email sent successfully</div>
                    <div className="text-xs mt-1">
                      Recipient: <code>{lastResult.to}</code> · Host: <code>{lastResult.smtp_host}</code> · From: <code>{lastResult.smtp_from}</code>
                    </div>
                    <div className="text-xs mt-1">
                      Check the inbox (and spam folder) to confirm delivery.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <div>
                    <div className="font-semibold">Test send failed</div>
                    <div className="text-xs mt-1 font-mono break-words">
                      {lastResult.error || 'Unknown error'}
                    </div>
                    <div className="text-xs mt-2 text-red-800/80">
                      Likely causes: expired/revoked SendGrid API key, unverified sender domain,
                      or wrong SMTP credentials. Update <code>SMTP_PASSWORD</code> in environment
                      and re-deploy.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent failures */}
        {status?.recent_failures?.length > 0 && (
          <div className="border-t pt-4">
            <div className="text-sm font-semibold text-slate-800 mb-2">
              Recent delivery failures
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {status.recent_failures.map((f, i) => (
                <div
                  key={f.id || i}
                  className="text-xs bg-slate-50 border rounded p-2 font-mono"
                >
                  <div className="font-semibold text-slate-900">
                    {new Date(f.created_at).toLocaleString()} → {f.to}
                  </div>
                  <div className="text-slate-700 truncate">{f.subject}</div>
                  <div className="text-red-700 mt-0.5 break-words">{f.error}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
